import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Value of `type` on each filter definition (e.g. `SINGLE`, `MULTIPLE`).
 */
export type ListFieldFilterFieldType = "SINGLE" | "MULTIPLE" | string;

export type ListFieldFilterValueByField = Record<string, string | string[]>;

export type ListFieldFiltersContextValue = {
  filterValues: Readonly<ListFieldFilterValueByField>;
  setFilterValue: (
    field: string,
    filterType: ListFieldFilterFieldType,
    value: string | string[] | null,
  ) => void;
  toggleFilterOption: (
    field: string,
    filterType: ListFieldFilterFieldType,
    optionValue: string,
  ) => void;
  clearField: (field: string) => void;
  clearAll: () => void;
  getSelection: (
    field: string,
    filterType: ListFieldFilterFieldType,
  ) => string | string[] | undefined;
  isFieldActive: (
    field: string,
    filterType: ListFieldFilterFieldType,
  ) => boolean;
  activeFieldCount: number;
  getQueryStringForField: (field: string) => string;
};

const ListFieldFiltersContext = createContext<
  ListFieldFiltersContextValue | undefined
>(undefined);

export function isMultipleFilterType(t: ListFieldFilterFieldType): boolean {
  return String(t).toUpperCase() === "MULTIPLE" || String(t) === "MULTI";
}

function normalizeIncomingValue(
  filterType: ListFieldFilterFieldType,
  value: string | string[] | null,
): string | string[] | null {
  if (value === null) {
    return null;
  }
  if (isMultipleFilterType(filterType)) {
    if (Array.isArray(value)) {
      return value.length ? Array.from(new Set(value)) : null;
    }
    return value ? [value] : null;
  }
  if (Array.isArray(value)) {
    const first = value[0];
    return first === undefined || first === "" ? null : first;
  }
  return value === "" ? null : value;
}

export function ListFieldFiltersProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [filterValues, setFilterValues] = useState<ListFieldFilterValueByField>(
    {},
  );

  const setFilterValue = useCallback(
    (
      field: string,
      filterType: ListFieldFilterFieldType,
      value: string | string[] | null,
    ) => {
      setFilterValues((prev) => {
        const next = { ...prev };
        const normalized = normalizeIncomingValue(filterType, value);
        if (normalized === null) {
          delete next[field];
        } else {
          next[field] = normalized;
        }
        return next;
      });
    },
    [],
  );

  const toggleFilterOption = useCallback(
    (
      field: string,
      filterType: ListFieldFilterFieldType,
      optionValue: string,
    ) => {
      if (isMultipleFilterType(filterType)) {
        setFilterValues((prev) => {
          const next = { ...prev };
          const current = (prev[field] as string[] | undefined) ?? [];
          const isOn = current.includes(optionValue);
          const updated = isOn
            ? current.filter((v) => v !== optionValue)
            : [...current, optionValue];
          if (updated.length === 0) {
            delete next[field];
          } else {
            next[field] = updated;
          }
          return next;
        });
        return;
      }

      setFilterValues((prev) => {
        const next = { ...prev };
        const current = prev[field] as string | undefined;
        if (current === optionValue) {
          delete next[field];
        } else {
          next[field] = optionValue;
        }
        return next;
      });
    },
    [],
  );

  const clearField = useCallback((field: string) => {
    setFilterValues((prev) => {
      if (!(field in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setFilterValues({});
  }, []);

  const getSelection = useCallback(
    (field: string, filterType: ListFieldFilterFieldType) => {
      const raw = filterValues[field];
      if (raw === undefined) {
        return undefined;
      }
      if (isMultipleFilterType(filterType)) {
        return Array.isArray(raw) ? raw : [raw];
      }
      if (Array.isArray(raw)) {
        return raw[0];
      }
      return raw;
    },
    [filterValues],
  );

  const isFieldActive = useCallback(
    (field: string, filterType: ListFieldFilterFieldType) => {
      const s = getSelection(field, filterType);
      if (s === undefined) {
        return false;
      }
      if (Array.isArray(s)) {
        return s.length > 0;
      }
      return s !== "";
    },
    [getSelection],
  );

  const activeFieldCount = useMemo(() => {
    return Object.entries(filterValues).filter(([, v]) => {
      if (Array.isArray(v)) {
        return v.length > 0;
      }
      return typeof v === "string" && v !== "";
    }).length;
  }, [filterValues]);

  const getQueryStringForField = useCallback(
    (field: string) => {
      const raw = filterValues[field];
      if (raw === undefined) {
        return "";
      }
      if (Array.isArray(raw)) {
        return raw.join(",");
      }
      return raw;
    },
    [filterValues],
  );

  const value = useMemo(
    () =>
      ({
        filterValues,
        setFilterValue,
        toggleFilterOption,
        clearField,
        clearAll,
        getSelection,
        isFieldActive,
        activeFieldCount,
        getQueryStringForField,
      }) satisfies ListFieldFiltersContextValue,
    [
      filterValues,
      setFilterValue,
      toggleFilterOption,
      clearField,
      clearAll,
      getSelection,
      isFieldActive,
      activeFieldCount,
      getQueryStringForField,
    ],
  );

  return (
    <ListFieldFiltersContext.Provider value={value}>
      {children}
    </ListFieldFiltersContext.Provider>
  );
}

export function useListFieldFilters() {
  const ctx = useContext(ListFieldFiltersContext);
  if (ctx === undefined) {
    throw new Error(
      "useListFieldFilters must be used within ListFieldFiltersProvider",
    );
  }
  return ctx;
}
